const BASE_URL = `${process.env.REACT_APP_API_URL || "http://localhost:5154"}/api`;

/* ─── Auth headers ─────────────────────────────────────────────────────── */
const getHeaders = () => {
  const token = localStorage.getItem("divyayoga_token");
  // X-Session-Token lets the backend tell which of the listed sessions is this
  // device, and keeps "revoke all others" from signing you out of your own tab.
  const sessionToken = localStorage.getItem("divyayoga_session");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(sessionToken ? { "X-Session-Token": sessionToken } : {}),
  };
};

/* ─── Request wrapper ──────────────────────────────────────────────────── */
const request = async (method, path, body = null) => {
  const options = { method, headers: getHeaders() };
  if (body) options.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, options);
  } catch (err) {
    throw new Error(`Network error: is the server reachable? (${err.message})`);
  }

  // Parse JSON safely
  let raw;
  try {
    raw = await res.json();
  } catch {
    throw new Error(`Server returned non-JSON response (HTTP ${res.status})`);
  }

  // Normalise casing: accept both { success, data } and { Success, Data }
  const success = raw?.success ?? raw?.Success;
  const message = raw?.message ?? raw?.Message ?? "";
  const data    = raw?.data    ?? raw?.Data    ?? null;

  // HTTP-level errors (401, 403, 500 …)
  if (!res.ok) {
    throw new Error(message || `HTTP ${res.status}`);
  }

  // App-level errors (success: false)
  if (success === false) {
    throw new Error(message || "Request failed");
  }

  return { success: true, data, message };
};

/* ══════════════════════════════════════════════════════════════════════════
   ADMIN API
══════════════════════════════════════════════════════════════════════════ */
export const adminApi = {

  /* ── Dashboard ─────────────────────────────────────────────────────────── */
  getDashboardStats: () =>
    request("GET", "/admin/dashboard/stats"),

  getAnalytics: (range = "12m") =>
    request("GET", `/admin/analytics?range=${encodeURIComponent(range)}`),

  getPriorityAlerts: (segment = "") =>
    request("GET", `/admin/priority-alerts${segment ? `?segment=${encodeURIComponent(segment)}` : ""}`),

  /* ── Users ─────────────────────────────────────────────────────────────── */
  getUsers: ({ search = "", status = "", page = 1, pageSize = 20 } = {}) => {
    const p = new URLSearchParams({ page, pageSize });
    if (search) p.set("search", search);
    if (status) p.set("status", status);
    return request("GET", `/admin/users?${p}`);
  },

  getUser: (id) =>
    request("GET", `/admin/users/${id}`),

  checkUserUniqueness: ({ email = "", phone = "", username = "" } = {}) => {
    const p = new URLSearchParams();
    if (email) p.set("email", email);
    if (phone) p.set("phone", phone);
    if (username) p.set("username", username);
    return request("GET", `/admin/users/check-uniqueness?${p}`);
  },
  createUser: (body) =>
    request("POST", "/admin/users", body),

  updateUser: (id, body) =>
    request("PUT", `/admin/users/${id}`, body),

  toggleUserStatus: (id) =>
    request("PATCH", `/admin/users/${id}/toggle-status`),

  deleteUser: (id) =>
    request("DELETE", `/admin/users/${id}`),

  getUserSessions: (id) =>
    request("GET", `/admin/users/${id}/sessions`),

  /* ── My own account (settings page) ─────────────────────────────────────── */
  getMyProfile: () =>
    request("GET", "/staff/profile"),

  updateMyProfile: (body) =>
    request("PUT", "/staff/profile", body),

  changeMyPassword: (body) =>
    request("PUT", "/staff/change-password", body),

  getMySessions: () =>
    request("GET", "/staff/sessions"),

  revokeMySession: (id) =>
    request("DELETE", `/staff/sessions/${id}`),

  revokeMyOtherSessions: () =>
    request("DELETE", "/staff/sessions"),

  /* ── Staff (admins, superadmins, trainers) ──────────────────────────────── */
  getStaff: () =>
    request("GET", "/admin/staff"),

  createStaff: (body) =>
    request("POST", "/admin/staff", body),

  toggleStaffStatus: (id) =>
    request("PATCH", `/admin/staff/${id}/toggle-status`),

  /* ── Packages ───────────────────────────────────────────────────────────── */
  getPackages: () =>
    request("GET", "/admin/packages"),

  createPackage: (body) =>
    request("POST", "/admin/packages", body),

  updatePackage: (id, body) =>
    request("PUT", `/admin/packages/${id}`, body),

  deletePackage: (id) =>
    request("DELETE", `/admin/packages/${id}`),

  togglePackageStatus: (id) =>
    request("PATCH", `/admin/packages/${id}/toggle-status`),

  hardDeletePackage: (id) =>
    request("DELETE", `/admin/packages/${id}/hard-delete`),

  /* ── Subscriptions ──────────────────────────────────────────────────────── */
  getSubscriptionStats: () =>
    request("GET", "/admin/subscriptions/stats"),

  getSubscriptions: ({ status = "", search = "", period = "", page = 1, pageSize = 50 } = {}) => {
    const p = new URLSearchParams({ page, pageSize });
    if (status) p.set("status", status);
    if (search) p.set("search", search);
    if (period) p.set("period", period);
    return request("GET", `/admin/subscriptions?${p}`);
  },

  createSubscription: (body) =>
    request("POST", "/admin/subscriptions", body),

  extendSubscription: (arg1, arg2) => {
    let payload = {};
    if (typeof arg1 === "object" && arg1 !== null) {
      payload = {
        subscriptionId: arg1.subscriptionId ?? arg1.SubscriptionId ?? null,
        userId: Number(arg1.userId ?? arg1.UserId ?? 0),
        days: Number(arg1.days ?? arg1.Days ?? 30)
      };
    } else {
      payload = {
        subscriptionId: typeof arg1 === "number" && arg1 > 0 ? arg1 : null,
        userId: 0,
        days: typeof arg2 === "number" ? arg2 : 30
      };
    }
    return request("POST", "/admin/subscriptions/extend", payload);
  },

  cancelSubscription: (id) =>
    request("PATCH", `/admin/subscriptions/${id}/cancel`),

  resetSubscriptions: () =>
    request("DELETE", "/admin/subscriptions/reset"),

  /* ── Yoga Classes ───────────────────────────────────────────────────────── */
  getYogaClasses: () =>
    request("GET", "/admin/yoga-classes"),

  createYogaClass: (body) =>
    request("POST", "/admin/yoga-classes", body),

  updateYogaClass: (id, body) =>
    request("PUT", `/admin/yoga-classes/${id}`, body),

  deleteYogaClass: (id) =>
    request("DELETE", `/admin/yoga-classes/${id}`),

  /* ── Attendance ─────────────────────────────────────────────────────────── */
  getAttendance: ({ userId, classId, date, page = 1, pageSize = 30 } = {}) => {
    const p = new URLSearchParams({ page, pageSize });
    if (userId)  p.set("userId",  userId);
    if (classId) p.set("classId", classId);
    if (date)    p.set("date",    date);
    return request("GET", `/admin/attendance?${p}`);
  },

  markAttendance: (body) =>
    request("POST", "/admin/attendance", body),

    /* ── Notifications ──────────────────────────────────────────────────────── */
  sendNotification: (body) =>
    request("POST", "/notification/send", body),
  
  /* ── Sessions ───────────────────────────────────────────────────────────── */
  revokeSession: (sessionId) =>
    request("DELETE", `/admin/sessions/${sessionId}`),

  /* ── Appearance & Background Audio ──────────────────────────────────────── */
  uploadAudio: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("divyayoga_token");
    const sessionToken = localStorage.getItem("divyayoga_session");
    const res = await fetch(`${BASE_URL}/admin/settings/upload-audio`, {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(sessionToken ? { "X-Session-Token": sessionToken } : {}),
      },
      body: formData,
    });
    const raw = await res.json();
    if (!res.ok || raw.success === false) {
      throw new Error(raw.message || "Audio upload failed");
    }
    return { success: true, data: raw.data || raw.Data, message: raw.message || raw.Message };
  },

  getAppearanceSettings: () =>
    request("GET", "/admin/settings/appearance"),

  updateAppearanceSettings: async (body) => {
    const res = await request("POST", "/admin/settings/appearance", body);
    try {
      localStorage.setItem("divyayoga_appearance", JSON.stringify(body));
      window.dispatchEvent(new CustomEvent("divyayoga-appearance-updated", { detail: body }));
    } catch {}
    return res;
  },

  /* ── User Queries & Contact Inquiries ──────────────────────────────────── */
  getQueries: ({ search = "", status = "", inquiryType = "", page = 1, pageSize = 15 } = {}) => {
    const p = new URLSearchParams({ page, pageSize });
    if (search) p.set("search", search);
    if (status && status !== "all") p.set("status", status);
    if (inquiryType && inquiryType !== "all") p.set("inquiryType", inquiryType);
    return request("GET", `/admin/queries?${p}`);
  },

  getQuery: (id) =>
    request("GET", `/admin/queries/${id}`),

  updateQueryStatus: (id, { status, adminNotes } = {}) =>
    request("PUT", `/admin/queries/${id}/status`, { status, adminNotes }),

  deleteQuery: (id) =>
    request("DELETE", `/admin/queries/${id}`),

  replyToQuery: (id, { subject, message, updateStatusTo } = {}) =>
    request("POST", `/admin/queries/${id}/reply`, { subject, message, updateStatusTo }),
};

export default adminApi;